import type { FC, ReactNode } from 'react'
import type { HomePageContent } from '@/types'

import { ArrowDown, Waves } from 'lucide-react'

interface HeroSectionProps {
    page: HomePageContent
}

const HeroSection: FC<HeroSectionProps> = ({ page }): ReactNode => {
    return (
        <section
            id='hero'
            aria-labelledby='hero-title'
            className='relative isolate flex min-h-svh items-center justify-center overflow-hidden px-6 pt-28 pb-40 sm:pb-48'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-x-0 top-0 -z-10 h-[70vh] bg-[radial-gradient(ellipse_at_top,var(--accent),transparent_65%)]'
            />

            <div className='mx-auto w-full max-w-3xl text-center'>
                {page.heroEyebrow && (
                    <p className='inline-flex items-center gap-2 rounded-full border bg-card/70 px-4 py-1.5 text-sm font-medium text-primary shadow-sm backdrop-blur-sm animate-in fade-in fill-mode-both duration-700 ease-out motion-reduce:animate-none'>
                        <Waves aria-hidden='true' className='size-4' />
                        <span>{page.heroEyebrow}</span>
                    </p>
                )}

                <h1
                    id='hero-title'
                    className='mt-8 font-heading text-5xl leading-[1.05] font-normal tracking-tight text-balance text-foreground sm:text-6xl md:text-7xl animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-150 duration-700 ease-out motion-reduce:animate-none'
                >
                    {page.heroTitle && <span>{page.heroTitle}</span>}
                    {page.heroTitle && page.heroTitleEmphasis && ' '}
                    {page.heroTitleEmphasis && <em className='italic text-primary'>{page.heroTitleEmphasis}</em>}
                </h1>

                {page.heroBody && (
                    <p className='mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-pretty text-muted-foreground sm:text-xl animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-300 duration-700 ease-out motion-reduce:animate-none'>
                        {page.heroBody}
                    </p>
                )}

                {page.heroButtonLabel && (
                    <a
                        href='#intro'
                        className='mt-10 inline-flex h-12 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:ring-4 focus-visible:ring-ring/40 focus-visible:outline-none animate-in fade-in fill-mode-both delay-500 duration-700 ease-out motion-reduce:animate-none'
                    >
                        <span>{page.heroButtonLabel}</span>
                        <ArrowDown aria-hidden='true' className='size-4' />
                    </a>
                )}
            </div>

            <svg
                aria-hidden='true'
                viewBox='0 0 1440 320'
                preserveAspectRatio='none'
                className='pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-32 w-full text-primary sm:h-40 md:h-52'
            >
                <path
                    fill='currentColor'
                    fillOpacity='0.06'
                    d='M0,192 C180,144 360,128 540,160 C720,192 900,256 1080,240 C1260,224 1350,176 1440,160 L1440,320 L0,320 Z'
                />
                <path
                    fill='currentColor'
                    fillOpacity='0.1'
                    d='M0,240 C200,208 400,200 600,224 C800,248 1000,288 1200,272 C1320,262 1390,248 1440,240 L1440,320 L0,320 Z'
                />
                <path
                    fill='currentColor'
                    fillOpacity='0.16'
                    d='M0,288 C240,268 480,264 720,280 C960,296 1200,304 1440,288 L1440,320 L0,320 Z'
                />
            </svg>
        </section>
    )
}

export default HeroSection
